import React, { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import SignUp from "./pages/SignUp";
import { Route, Routes } from "react-router-dom";
import Login from "./pages/Login";
import ChangePassword from "./pages/ChangePassword";
import ResetPassword from "./pages/ResetPassword";
import CreatePassword from "./pages/CreatePassword";
import LoginOtp from "./pages/LoginOtp";
import SignUpOtp from "./pages/SignUpOtp";
import Sidebar from "./components/Sidebar";
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import Transactions from "./pages/Transactions/Transactions";
import TransactionNav from "./components/TransactionNav";
import Refund from "./pages/Transactions/Refund";
import Chargebacks from "./pages/Transactions/Chargebacks";
import PaymentNav from "./components/PaymentNav";
import Transfer from "./pages/Payments/Transfer";
import PaymentLinks from "./pages/Payments/PaymentLinks";
import Ussd from "./pages/Payments/Ussd";
import Card from "./pages/Payments/Card";
import SettingsNav from "./components/SettingsNav";
import Profile from "./pages/Settings/Profile";
import Business from "./pages/Settings/Business";
import Settlement from "./pages/Settings/Settlement";
import ApiKeys from "./pages/Settings/ApiKeys";
import Webhook from "./pages/Settings/Webhook";
import DisputeLog from "./pages/DisputeLog";
import Notification from "./pages/Notification";
import { Toaster } from "react-hot-toast";
import PrivateRoute from "./components/PrivateRoute";
import { useAuth } from "./context/AuthContextProvider";
import ConfirmEmail from "./pages/ConfirmEmail";
import CheckMail from "./pages/CheckMail";
import KycUpdate from "./pages/Settings/BusinessPage/KycUpdate";
import Password from "./pages/Settings/ProfilePage/Password";
import UploadCAC from "./pages/Settings/BusinessPage/UploadCAC";
import UploadDoc from "./pages/Settings/BusinessPage/UploadDoc";
// import ChangePin from "./pages/Settings/ProfilePage/UploadCAC";
// import ResetPin from "./pages/Settings/ProfilePage/UploadDoc";
import ProfileSetting from "./pages/Settings/ProfilePage/ProfileSetting";
import PasswordReset from "./pages/PasswordReset";
import BusinessPayment from "./pages/Settings/BusinessPage/BusinessPayment";
import BusinessNotification from "./pages/Settings/BusinessPage/BusinessNotification";
import BusinessSecurity from "./pages/Settings/BusinessPage/BusinessSecurity";
import AboutBusiness from "./pages/Settings/BusinessPage/AboutBusiness";
import Permission from "./pages/Settings/Permission/Permission";
import CreateAccount from "./pages/CreateAccount";
import CompleteRegistration from "./pages/CompleteRegistration";
import NotActivated from "./pages/NotActivated";
import ViewDocs from "./pages/Settings/BusinessPage/ViewDocs";
import MigrateMerchant from "./pages/MigrateMerchant";
import UploadProof from "./pages/Settings/BusinessPage/UploadProof";
import CreateLink from "./pages/Payments/CreateLink";
import PaymentLink from "./pages/Payments/PaymentLink";
import EditLink from "./pages/Payments/EditLink";
import PaymentLinkTransactions from "./pages/Payments/PaymentLinkTransactions";
import DigitalInvoice from "./pages/Payments/DigitalInvoice";
import CreateInvoice from "./pages/Payments/CreateInvoice";
import PaymentInvoice from "./pages/Payments/PaymentInvoice";
import SettlementReport from "./pages/Settlement/SettlementReport";
import SettlementNav from "./components/SettlementNav";
import Subscriptions from "./pages/Subscriptions/Subscriptions";

import SplitPayment from "./pages/SplitPayment/SplitPayment";

const queryClient = new QueryClient();


function App() {
  const [inactive, setInactive] = useState(false);
  const { logout } = useAuth();

  // Timeout duration for inactivity (5 minutes in milliseconds)
  const INACTIVE_TIMEOUT = 300000;
  let inactivityTimer;

  // Function to handle user inactivity
  const handleInactive = () => {
    setInactive(true);
    logout();
  };

  // Function to reset the inactivity timer
  const resetTimer = () => {
    clearTimeout(inactivityTimer);
    if (!inactive) {
      inactivityTimer = setTimeout(handleInactive, INACTIVE_TIMEOUT);
    }
  };

  // Function to logout and clear storage

  useEffect(() => {
    // Initial setup of the inactivity timer
    inactivityTimer = setTimeout(handleInactive, INACTIVE_TIMEOUT);

    // Event listeners to detect user activity
    const events = ["mousemove", "keydown", "mousedown", "touchstart"];
    const resetEvents = () => {
      if (inactive) {
        setInactive(false);
      }
      resetTimer();
    };
    events.forEach((event) => {
      window.addEventListener(event, resetEvents);
    });

    // Cleanup function to remove event listeners and clear the timer
    return () => {
      clearTimeout(inactivityTimer);
      events.forEach((event) => {
        window.removeEventListener(event, resetEvents);
      });
    };
  }, [inactive]); // Added inactive dependency to re-run effect when inactive state changes

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <Toaster position="top-center" />
        <Routes>
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/" element={<Login />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/resetPassword" element={<PasswordReset />} />
          <Route path="/create-password" element={<CreatePassword />} />
          <Route path="/login-otp" element={<LoginOtp />} />
          <Route path="/not-activated" element={<NotActivated />} />
          <Route
            path="/merchant-migration/:email"
            element={<MigrateMerchant />}
          />
          <Route
            path="/complete-registration/:email"
            element={<CompleteRegistration />}
          />
          <Route path="/signup-otp" element={<SignUpOtp />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />
          <Route path="/check-email" element={<CheckMail />} />
          <Route
            path="/complete-invitation/:email"
            element={<CreateAccount />}
          />
          <Route element={<PrivateRoute />}>
            <Route path="/*" element={<Sidebar />}>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="users" element={<Users />} />
              <Route path="dispute" element={<DisputeLog />} />
              <Route path="notification" element={<Notification />} />
            </Route>
            <Route path="subscriptions" element={<Sidebar />}>
              <Route path="" element={<Subscriptions />} />
            </Route>
            <Route path="transactions" element={<TransactionNav />}>
              <Route path="" element={<Transactions />} />
              <Route path="refunds" element={<Refund />} />
              <Route path="chargebacks" element={<Chargebacks />} />
            </Route>
            <Route path="settlement-report" element={<SettlementNav />}>
              <Route path="" element={<SettlementReport />} />
            </Route>
            <Route path="payments" element={<PaymentNav />}>
              <Route path="digital-invoice">
                <Route path="" element={<DigitalInvoice />} />
                <Route path="create-invoice" element={<CreateInvoice />} />
                <Route
                  path="payment-invoice/:code"
                  element={<PaymentInvoice />}
                />
              </Route>

              {/* <Route path="" element={<Transfer />} /> */}
              {/* <Route path="card" element={<Card />} /> */}
              <Route path="payment-links">
                <Route path="" element={<PaymentLinks />} />
                <Route path="create-link" element={<CreateLink />} />
                <Route path="payment-link/:id" element={<PaymentLink />} />
                <Route path="edit-link/:id" element={<EditLink />} />
                <Route
                  path="transaction/:id"
                  element={<PaymentLinkTransactions />}
                />
              </Route>
              <Route path="ussd" element={<Ussd />} />
              <Route path="split-payment" element={<SplitPayment />} />
            </Route>
            <Route path="settings" element={<SettingsNav />}>
              <Route path="" element={<Profile />}>
                <Route path="profile">
                  <Route path="" element={<ProfileSetting />} />
                  {/* <Route path="password" element={<Password />} /> */}
                  {/* <Route path="kyc-update" element={<KycUpdate />} /> */}
                  {/* <Route path="upload-cac" element={<UploadCAC />} />
                  <Route path="upload-doc" element={<UploadDoc />} /> */}
                  {/* <Route path="change-pin" element={<ChangePin />} /> */}
                  {/* <Route path="reset-pin" element={<ResetPin />} /> */}
                </Route>
              </Route>

              <Route element={<Business />}>
                <Route path="business">
                  <Route path="" element={<AboutBusiness />} />
                  <Route
                    path="notifications"
                    element={<BusinessNotification />}
                  />
                  <Route path="pss-update" element={<KycUpdate />} />
                  <Route path="upload-cac" element={<UploadCAC />} />
                  <Route path="upload-doc" element={<UploadDoc />} />
                  <Route
                    path="upload-proof-of-address"
                    element={<UploadProof />}
                  />
                  <Route path="security" element={<BusinessSecurity />} />
                  <Route path="payments" element={<BusinessPayment />} />
                  <Route path="view-document" element={<ViewDocs />} />
                </Route>
              </Route>
              <Route path="settlement" element={<Settlement />} />
              <Route path="permission" element={<Permission />} />
              <Route path="api-keys" element={<ApiKeys />} />
              <Route path="web-hook" element={<Webhook />} />
            </Route>
          </Route>
        </Routes>
      </QueryClientProvider>
    </>
  );
}

export default App;
